import { SalesQuotePriceListResponseDto } from "@o2-fe/api";

/**
 * Single source of truth for the display ordering of quote price lists,
 * shared by the quote sheet, the default docx template and the custom
 * template, so that the exported document always mirrors what the user sees
 * (and arranged via drag and drop) on screen.
 *
 * Rules:
 * - categories ordered by `categoryOrder`, falling back to alphanumeric
 *   category code for legacy quotes that lack ordering fields;
 * - items ordered by `order` within their category, falling back to
 *   alphanumeric item code.
 */

/**
 * A category group of quote price lists, in display order.
 */
export interface OrderedQuotePriceListCategoryGroup {
  /** The category id. */
  id: string;
  /** The category code. */
  code: string;
  /** The category name. */
  name: string;
  /** The items of the category, in display order. */
  items: SalesQuotePriceListResponseDto[];
}

/**
 * Compares two codes alphanumerically (numeric-aware), e.g. "C2" < "C10".
 *
 * @param a - First code
 * @param b - Second code
 * @returns Negative, zero or positive per standard comparator contract
 */
export function compareCodesAlphanumeric(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

/**
 * Comparator for the full display order of quote price lists: category
 * position first (with alphanumeric fallback), then item position within the
 * category (with alphanumeric fallback).
 *
 * @param a - First quote price list
 * @param b - Second quote price list
 * @returns Negative, zero or positive per standard comparator contract
 */
export function compareQuotePriceLists(
  a: SalesQuotePriceListResponseDto,
  b: SalesQuotePriceListResponseDto,
): number {
  const categoryOrderA = a.categoryOrder ?? Number.POSITIVE_INFINITY;
  const categoryOrderB = b.categoryOrder ?? Number.POSITIVE_INFINITY;
  if (categoryOrderA !== categoryOrderB) {
    return categoryOrderA - categoryOrderB;
  }

  const categoryCodeComparison = compareCodesAlphanumeric(
    a.category?.code ?? "",
    b.category?.code ?? "",
  );
  if (categoryCodeComparison !== 0) {
    return categoryCodeComparison;
  }

  const orderA = a.order ?? Number.POSITIVE_INFINITY;
  const orderB = b.order ?? Number.POSITIVE_INFINITY;
  if (orderA !== orderB) {
    return orderA - orderB;
  }

  return compareCodesAlphanumeric(a.code ?? "", b.code ?? "");
}

/**
 * Returns a copy of the given quote price lists sorted in display order.
 *
 * @param priceLists - The quote price lists to sort
 * @returns A new array sorted in display order
 */
export function sortQuotePriceLists(
  priceLists: SalesQuotePriceListResponseDto[],
): SalesQuotePriceListResponseDto[] {
  return [...priceLists].sort(compareQuotePriceLists);
}

/**
 * Definitive position of a single quote price list within an ordering
 * snapshot: item position within its category + category position within
 * the quote.
 */
export interface QuotePriceListOrderingEntry {
  /** The quote price list id (temporary ids allowed in create mode). */
  id: string;
  /** Display position of the item within its category. */
  order: number;
  /** Display position of the item's category within the quote. */
  categoryOrder: number;
}

/**
 * Computes the full ordering snapshot from category groups in display
 * order: each item gets its index within the group as `order` and the group
 * index as `categoryOrder`.
 *
 * @param groups - Category groups in display order
 * @returns One ordering entry per item
 */
export function computeOrderingSnapshot(
  groups: { items: SalesQuotePriceListResponseDto[] }[],
): QuotePriceListOrderingEntry[] {
  return groups.flatMap((group, categoryIndex) =>
    group.items.map((item, itemIndex) => ({
      id: item.id,
      order: itemIndex,
      categoryOrder: categoryIndex,
    })),
  );
}

/**
 * Returns a copy of the given quote price lists with the ordering fields of
 * the snapshot applied (items not present in the snapshot are left
 * untouched).
 *
 * @param priceLists - The quote price lists to update
 * @param entries - The ordering snapshot to apply
 * @returns A new array with updated ordering fields
 */
export function applyOrderingSnapshot(
  priceLists: SalesQuotePriceListResponseDto[],
  entries: QuotePriceListOrderingEntry[],
): SalesQuotePriceListResponseDto[] {
  const entryById = new Map(entries.map((entry) => [entry.id, entry]));
  return priceLists.map((priceList) => {
    const entry = entryById.get(priceList.id);
    return entry
      ? {
          ...priceList,
          order: entry.order,
          categoryOrder: entry.categoryOrder,
        }
      : priceList;
  });
}

/**
 * Groups quote price lists by category, with both the categories and the
 * items of each category in display order.
 *
 * @param priceLists - The quote price lists to group
 * @returns The ordered category groups
 */
export function groupQuotePriceListsByCategory(
  priceLists: SalesQuotePriceListResponseDto[],
): OrderedQuotePriceListCategoryGroup[] {
  const sorted = sortQuotePriceLists(priceLists);

  // Map preserves insertion order, so iterating the sorted list yields the
  // categories already in display order.
  const groupsByCategoryId = new Map<
    string,
    OrderedQuotePriceListCategoryGroup
  >();
  for (const priceList of sorted) {
    const categoryId = priceList.category?.id ?? "";
    let group = groupsByCategoryId.get(categoryId);
    if (!group) {
      group = {
        id: categoryId,
        code: priceList.category?.code ?? "",
        name: priceList.category?.name ?? "",
        items: [],
      };
      groupsByCategoryId.set(categoryId, group);
    }
    group.items.push(priceList);
  }

  return Array.from(groupsByCategoryId.values());
}
